// The api route that counts one recipient funnel event. It lives in the designated browser-endpoint
// source because the e2e specs and every mocked route fixture read the same path; a second copy is what
// `deployed-topology-sweep` exists to refuse.
import {RECIPIENT_FUNNEL_EVENTS_PATH} from "../../api/endpointPaths.mjs";
// The shared anonymous fire-and-forget transport — the same one the consent, download, vitals and
// personalization counters use, so this counter keeps their promises rather than restating them.
import {postFirstPartyBeacon} from "../../api/firstPartyBeacon";
import type {AttributionChannel} from "attribution/adAttribution";
import type {SendEventEmitter} from "home/sendEvents";
import {arrivedFromRecipientPage, type RecipientArrival} from "./recipientArrival";
import type {RecipientFunnelEmitter, RecipientFunnelEvent, Unsubscribe} from "./recipientFunnelEvents";

/**
 * The telemetry SUBSCRIBER for the recipient acquisition funnel (ui#1973).
 *
 * Separate from `./recipientFunnelEvents` on purpose (owner 2026-08-27): the download page and the
 * composer emit domain events and know nothing about where they go, so measuring the funnel is never a
 * reason to reach into either. This module is the whole of "where they go".
 *
 * Fire-and-forget, uncorrelatable and never retried, exactly like its siblings: a retry would
 * double-count precisely the events that did arrive, and these counters carry no request identity to
 * deduplicate with. `postFirstPartyBeacon` omits credentials, so no Firebase bearer and no anonymous uid
 * rides along — which is the difference between this and the offer family's authenticated beacon, and
 * the reason that one could not be reused for a reader who never signs in.
 */

/** The complete request body: one counter. There is nothing else to send — see the events module. */
export interface RecipientFunnelReport {
	counter: RecipientFunnelEvent["counter"];
}

/** One event as the wire carries it. A pure function, so the shape is testable without a transport. */
export function recipientFunnelReport(event: RecipientFunnelEvent): RecipientFunnelReport {
	return {counter: event.counter};
}

/**
 * Sends one event, and returns whether anything left the browser.
 *
 * Exported for the same reason the report builder is: "did this counter fire" is the thing worth
 * asserting, and it is not observable from the emitter alone.
 */
export function reportRecipientFunnelEvent(event: RecipientFunnelEvent): boolean {
	return postFirstPartyBeacon(RECIPIENT_FUNNEL_EVENTS_PATH, recipientFunnelReport(event));
}

/** Everything the wiring needs, passed in so it is drivable without touching module singletons. */
export interface RecipientFunnelWiring {
	/** The funnel's own emitter — the four counters. */
	funnel: RecipientFunnelEmitter;
	/** The composer's seam, which says only that a send began. */
	sends: SendEventEmitter;
	/** This document's arrival flag. */
	arrival: RecipientArrival;
	/**
	 * This document's acquisition bucket, as a function rather than a value: it is derived on first use
	 * and memoized, and calling it at wire time is what forces that derivation to happen while the
	 * landing is still this document's own.
	 */
	channel: () => AttributionChannel | undefined;
}

/**
 * Subscribes the beacon to the funnel, translates a started send into the funnel's own vocabulary, and
 * returns the unsubscribe.
 *
 * Wired from the app SHELL, which is the only host that outlives the whole funnel: the chip lives on the
 * download page and the send happens in the composer, and no component is mounted for both. A narrower
 * host would unsubscribe exactly where the funnel gets interesting.
 *
 * <b>The arrival is marked HERE, at wire time, and that ordering is load-bearing.</b> The flag has to be
 * set before any send can start, and this is the earliest point in the document that runs once. Marking
 * it inside the download page instead would miss the journey the funnel is actually for — the claim link
 * opens a NEW TAB, so the document that goes on to send never mounts a download page at all.
 *
 * <b>A started send is translated rather than forwarded.</b> The composer says "a send began"; whether
 * that is `recipient.send.first` is this funnel's question, answered by spending the arrival flag. That
 * is why the composer needs no knowledge of recipients, and why a second subscriber to the same send
 * event could count something else without disturbing this one.
 */
export function wireRecipientFunnelTelemetry(wiring: RecipientFunnelWiring): Unsubscribe {
	const {funnel, sends, arrival, channel} = wiring;
	if (arrivedFromRecipientPage(channel())) {
		arrival.mark();
	}
	const unsubscribeFunnel = funnel.subscribe(event => {
		reportRecipientFunnelEvent(event);
	});
	const unsubscribeSends = sends.subscribe(() => {
		if (arrival.consume()) {
			funnel.firstSend();
		}
	});
	return () => {
		unsubscribeSends();
		unsubscribeFunnel();
	};
}
